import GameSceneBase from './GameSceneBase';
import { KEYS } from 'game/constants/SceneConstants';
import { GameTimer } from '../../display/ui/games/GameTimer';
import { OnboardingPointer } from '../../display/ui/OnboardingPointer';

const TIME_LIMIT = 30000;
const CAROUSEL_ITEMS = [
  'friendliest_1',
  'friendliest_2',
  'friendliest_3',
  'friendliest_4',
  'friendliest_5',
  'friendliest_6',
  'friendliest_7',
  'friendliest_8',
  'friendly_1',
  'friendly_2',
  'friendly_3',
  'friendly_4',
  'friendly_5',
  'friendly_6',
  'booby_prize_1',
  'booby_prize_2',
  'booby_prize_3',
  'booby_prize_4',
];

export default class FriendlyFlora extends GameSceneBase {
  constructor() {
    super({ key: KEYS.FriendlyFlora, active: false });
  }

  create() {
    super.create();

    this.fixedOptionsOrder = Phaser.Utils.Array.Shuffle([...CAROUSEL_ITEMS]);
    this.spineBG = this.add.spine(840, 360, 'friendly_flora_game_background', '', true);
    this.itemContainer = this.add
      .spine(840, 360, 'friendly_flora_reticule', '', true)
      .setVisible(false);
    this.itemContainerGraphic = this.add.image(840, 150, 'friendly_flora', '').setVisible(false);
    this.clickbox = this.add.zone(0, 0, 1680, 720).setOrigin(0);
    this.gameRunning = false;
    this.gameClock = new GameTimer(this, 550, 0);
    this.gameClock.visible = false;

    this.onboardingPointer = new OnboardingPointer(this, {
      tapPositions: [{ x: 840, y: 150 }],
    });

    this.setupGame();
  }

  showInstruction() {
    super.showInstruction(this.game.copy.get('FriendlyFlora_instruction'), this.startGame, this);
  }

  setupGame() {
    this.carouselOptions = [...this.fixedOptionsOrder];

    this.slots = [];
    while (this.slots.length < 8) {
      const row = Math.floor(this.slots.length / 4);
      const x = (row === 0 ? 575 : 495) + (this.slots.length % 4) * (row === 0 ? 175 : 225);
      const y = 550 + row * 65;
      const slotItem = this.add
        .image(x, y, 'friendly_flora', 'removable_' + (this.slots.length + 1))
        .setOrigin(0.5, 1);
      slotItem.setScale(0);
      this.slots.push(slotItem);
      this.slots.forEach((s) => {
        s.setVisible(false);
      });
    }

    this.clickbox.setInteractive();

    // slot graphic that changes items
    this.itemContainerGraphic.visible = false;
    this.tweens.add({
      targets: this.slots,
      scale: 1,
      duration: 250,
      ease: Phaser.Math.Easing.Back.Out,
    });
    this.spineBG.once('complete', this.showInstruction, this);
    this.spineBG.play('intro');
    this.spineBG.on('event', this.handleSpineEvent, this);
  }

  handleSpineEvent(t, e) {
    if (e.data?.name === 'sfx') {
      switch (e.stringValue) {
        case 'page_open':
          this.game.soundController.playSFX('general', 'general_page_open');
          break;
        case 'page_close':
          this.game.soundController.playSFX('general', 'general_page_close');
          break;
        case 'page_popup':
          this.game.soundController.playSFX('general', 'general_popup');
          break;
      }
    }
  }
  restartGame() {
    this.create();
  }

  startGame() {
    this.timeRemaining = TIME_LIMIT;
    this.slotIndex = 0;

    this.activateSlot(this.slotIndex);
    this.onboardingPointer.enable();
    this.gameRunning = true;
    this.gameClock.visible = true;
    this.gameClock.enter();
    this.itemContainer.play('in');
    this.itemContainer.setVisible(true);
    this.slots.forEach((s) => {
      s.setVisible(true);
    });
  }

  activateSlot(index) {
    if (this.activeSlotTween) {
      this.activeSlotTween.remove();
      this.activeSlotTween = null;
    }
    this.activeSlotItem = this.slots[index];
    this.activeSlotTween = this.tweens.add({
      targets: this.activeSlotItem,
      scale: 1.1,
      yoyo: true,
      duration: 500,
      repeat: -1,
    });

    this.startSlotCarousel();
  }

  startSlotCarousel() {
    this.slotCarouselActive = {
      time: 10,
      ticks: 0,
      item: -1,
    };
    this.carouselFX = this.game.soundController.playSFX('friendlyflora', 'flora_carousel');
    this.changeSlotCarousel();
    this.clickbox.once('pointerdown', this.stopSlotCarousel, this);
  }

  updateSFX() {
    this.carouselFX.stop();
  }

  changeSlotCarousel() {
    this.slotCarouselActive.ticks++;
    this.slotCarouselActive.item++;
    this.itemContainerGraphic.visible = true;
    this.itemContainerGraphic.setFrame(
      this.carouselOptions[this.slotCarouselActive.item % this.carouselOptions.length]
    );
    this.itemContainerGraphic.setScale(
      Math.min(100 / this.itemContainerGraphic.width, 100 / this.itemContainerGraphic.height)
    );
    this.slotCarouselActive.time = 5 * this.slotCarouselActive.ticks;
  }

  stopSlotCarousel() {
    if (!this.slotCarouselActive) return;
    const carouselIndex = this.slotCarouselActive.item % this.carouselOptions.length;
    this.slotCarouselActive = false;
    this.activeSlotTween.stop();
    this.tweens.add({
      targets: this.activeSlotItem,
      scale: 0,
      yoyo: true,
      hold: 100,
      duration: 150,
      onComplete: this.handleSlotChanged,
      onCompleteScope: this,
      completeDelay: 1000,
      onYoyo: () => {
        this.activeSlotItem.setFrame(this.carouselOptions[carouselIndex]);
        this.carouselOptions.splice(carouselIndex, 1);
      },
    });
    this.updateSFX();
    this.game.soundController.playSFX('friendlyflora', 'flora_selection');
  }

  handleSlotChanged() {
    this.slotIndex++;
    if (this.slotIndex < this.slots.length) {
      this.activateSlot(this.slotIndex);
    } else {
      this.finishGame();
    }
  }

  update(t, delta) {
    if (this.gameRunning && this.slotCarouselActive) {
      this.slotCarouselActive.time -= delta;
      if (this.slotCarouselActive.time <= 0) {
        this.changeSlotCarousel();
      }
      if (this.timeRemaining > 0) {
        this.timeRemaining -= delta;
        this.gameClock.setTimePercent(this.timeRemaining / TIME_LIMIT);
        if (this.timeRemaining <= 0) this.finishGame();
      }
    }
  }

  finishGame() {
    this.gameRunning = false;
    this.game.soundController.stopMusic('music');
    this.game.soundController.stopAllSFX();
    this.onboardingPointer.disable();
    this.slotCarouselActive = false;
    this.gameClock.exit();
    this.clickbox.removeInteractive();
    if (this.activeSlotTween) {
      this.activeSlotTween.remove();
      this.activeSlotTween = null;
    }

    this.itemContainerGraphic.visible = false;
    this.tweens.add({
      targets: this.slots,
      scale: 0,
      duration: 100,
    });

    const count = this.slots.reduce((i, slot) => {
      if (slot.frame.name.includes('friendliest')) i++;
      if (slot.frame.name.includes('friendly')) i++;
      return i;
    }, 0);

    let stars = 0;
    if (count > 7) stars = 3;
    else if (count > 5) stars = 2;
    else if (count > 1) stars = 1;

    this.itemContainer.play('out');
    this.gameClock.exit();

    this.spineBG.once('complete', () => this.showEndGameScreen(stars >= 2, stars), this);
    this.game.soundController.stopAllSFX();
    if (stars >= 2) {
      this.spineBG.play('win');
      this.game.soundController.playSFX('general', 'general_success', {
        delay: 1.2,
      });
      this.floraSuccessFX = this.game.soundController.playSFX('friendlyflora', 'flora_success', {
        delay: 2.2,
      });

      console.log(this.floraSuccessFX);
    } else {
      this.spineBG.play('lose');
      this.game.soundController.playSFX('general', 'general_fail', {
        delay: 1.2,
      });
      this.floraFailFX = this.game.soundController.playSFX('friendlyflora', 'flora_fail', {
        delay: 2.2,
      });

      console.log(this.floraFailFX);
    }
  }

  shutdown() {
    this.game.soundController.stopAllSFX();
    super.shutdown();
    this.game.unloadGame(KEYS.FriendlyFlora);
  }
}
